import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Send, ArrowLeft, Paperclip, X, Download, Trash2, MoreVertical, Hash, Users, Settings, MessageCircle, Pencil, Check, SmilePlus, Search as SearchIcon, Pin, ArrowDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../ui/dropdown-menu';
import ProfileImage from '../common/ProfileImage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

const getInitials = (name = '') =>
  name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

const roleConfig = {
  admin:      { label: 'Admin',      className: 'bg-red-100 text-red-700 border-red-200' },
  instructor: { label: 'Instructor', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  student:    { label: 'Student',    className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const getDateLabel = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const chatQuillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ],
  keyboard: {
    bindings: {}  // We'll handle Enter ourselves
  }
};

const chatQuillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'link', 'image'
];

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

// Check if quill HTML content is effectively empty
const isQuillEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

// Sanitize message HTML for safe rendering
const sanitizeMessageHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 'img', 'span', 'mark'],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'style', 'class'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });
};

const highlightSearchTerm = (html, term) => {
  if (!term || !html) return html;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Only highlight text outside of HTML tags
  return html.replace(new RegExp(`(>[^<]*?)(${escaped})`, 'gi'), '$1<mark class="bg-yellow-200 dark:bg-yellow-700 rounded-sm px-0.5">$2</mark>')
    .replace(new RegExp(`^([^<]*?)(${escaped})`, 'gi'), '$1<mark class="bg-yellow-200 dark:bg-yellow-700 rounded-sm px-0.5">$2</mark>');
};

const ChatArea = ({
  conversation,
  messages,
  currentUser,
  onSendMessage,
  onBackToUsers,
  onDeleteMessage,
  onEditMessage,
  onReactToMessage,
  onPinMessage,
  onSearch,
  onClearSearch,
  searchResults,
  isSearching,
  onTyping,
  typingUsers,
  showChat,
  loading,
  hasMore,
  onLoadMore,
  onManageMembers
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [forceRender, setForceRender] = useState(0);
  const [currentPermissions, setCurrentPermissions] = useState({ canManage: false, isCreator: false, isAdmin: false });
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  // Clear search state when conversation changes
  useEffect(() => {
    setShowSearch(false);
    setSearchQuery('');
    onClearSearch?.();
  }, [conversation?.id]);

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    const newCount = messages?.length || 0;
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = newCount;

    // Only scroll to bottom when new messages are added, not on updates (reactions, edits, pins)
    if (newCount > prevCount) {
      if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;
    const handleScroll = () => {
      const distFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      setShowScrollBottom(distFromBottom > 200);
    };
    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [conversation?.id]);

  const scrollToBottom = () => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
  };

  const safeIdCompare = React.useCallback((a, b) => {
    if (!a || !b) return false;
    return String(typeof a === 'object' ? a.toString() : a) === String(typeof b === 'object' ? b.toString() : b);
  }, []);

  const memberManagementPermissions = React.useMemo(() => {
    if (!conversation || conversation.type !== 'custom' || !currentUser)
      return { canManage: false, isCreator: false, isAdmin: false };
    const creatorId = conversation.creator?._id;
    const userId = currentUser?.id;
    if (!creatorId || !userId) return { canManage: false, isCreator: false, isAdmin: false };
    const isCreator = safeIdCompare(creatorId, userId);
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'platform_admin';
    return { canManage: isCreator || isAdmin, isCreator, isAdmin };
  }, [conversation?.id, conversation?.creator?._id, conversation?.type, currentUser?.id, currentUser?.role, safeIdCompare]);

  useEffect(() => {
    if (JSON.stringify(currentPermissions) !== JSON.stringify(memberManagementPermissions)) {
      setCurrentPermissions(memberManagementPermissions);
      setForceRender(prev => prev + 1);
    }
  }, [memberManagementPermissions, currentPermissions]);

  const sendMessageRef = useRef(null);

  const handleSendMessage = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (isQuillEmpty(messageInput) && attachments.length === 0) return;
    const messageData = {
      content: messageInput,
      messageType: conversation.type === 'group' ? 'group' : conversation.type === 'custom' ? 'custom' : 'direct',
      ...(conversation.type === 'group'
        ? { groupId: conversation.id }
        : conversation.type === 'custom'
        ? { customConversationId: conversation.id }
        : { recipientId: conversation.id })
    };
    try {
      await onSendMessage(messageData, attachments.length > 0 ? attachments : null);
      setMessageInput('');
      setAttachments([]);
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.focus();
      }
    } catch (err) { console.error(err); }
  }, [messageInput, attachments, conversation, onSendMessage]);

  // Keep ref in sync so the keydown handler always calls the latest version
  sendMessageRef.current = handleSendMessage;

  // Handle Enter key in Quill editor
  const handleQuillKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageRef.current();
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setAttachments(prev => [...prev, ...files].slice(0, 5));
  };

  const canDeleteMessage = (message) =>
    message.sender?._id === currentUser.id ||
    ((conversation.type === 'group' || conversation.type === 'custom') &&
      (currentUser.role === 'instructor' || currentUser.role === 'admin' || currentUser.role === 'platform_admin'));

  const canPinMessage = () =>
    (conversation.type === 'group' || conversation.type === 'custom') &&
    (currentUser.role === 'instructor' || currentUser.role === 'admin' || currentUser.role === 'platform_admin' || memberManagementPermissions.isCreator);

  const handleDeleteMessage = (messageId) => {
    if (window.confirm('Delete this message?')) onDeleteMessage(messageId);
  };

  const handleStartEdit = (message) => {
    setEditingMessageId(message._id);
    setEditContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (messageId) => {
    if (isQuillEmpty(editContent)) return;
    try {
      await onEditMessage(messageId, editContent);
      setEditingMessageId(null);
      setEditContent('');
    } catch (err) { /* error handled in parent */ }
  };

  const renderAttachment = (attachment, index) => {
    const isImage = attachment.fileType === 'image';
    return (
      <div key={index} className="mt-2">
        {isImage ? (
          <img
            src={`data:${attachment.contentType};base64,${attachment.data}`}
            alt={attachment.fileName}
            className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => {
              const w = window.open('', '_blank');
              w.document.write(`<html><body style="margin:0;background:#000;display:flex;justify-content:center;align-items:center;min-height:100vh;"><img src="data:${attachment.contentType};base64,${attachment.data}" style="max-width:100%;max-height:100%;object-fit:contain;"/></body></html>`);
            }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="flex items-center gap-2 bg-black/10 rounded-lg px-3 py-2 max-w-[180px]">
            <Paperclip className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{attachment.fileName}</p>
              <p className="text-[10px] opacity-60">{formatFileSize(attachment.fileSize)}</p>
            </div>
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = `data:${attachment.contentType};base64,${attachment.data}`;
                link.download = attachment.fileName;
                link.click();
              }}
              className="opacity-70 hover:opacity-100"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Empty / No conversation state ────────────────────────────
  if (!conversation) {
    return (
      <div className={cn('flex-1 flex items-center justify-center bg-muted/30', showChat ? 'flex' : 'hidden md:flex')}>
        <div className="text-center px-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-10 h-10 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Your Messages</h3>
          <p className="text-sm text-muted-foreground">Select a conversation from the sidebar to get started</p>
        </div>
      </div>
    );
  }

  // ─── Build messages with date grouping ────────────────────────
  let lastDate = null;
  const renderedMessages = messages.map((message, idx) => {
    const senderId = message.sender?._id;
    const isOwn = senderId === currentUser.id;
    const msgDate = getDateLabel(message.createdAt);
    const showDate = msgDate !== lastDate;
    if (showDate) lastDate = msgDate;

    const prevSender = idx > 0 ? messages[idx - 1].sender?._id : null;
    const nextSender = idx < messages.length - 1 ? messages[idx + 1].sender?._id : null;
    const isFirstInGroup = senderId !== prevSender;
    const isLastInGroup = senderId !== nextSender;
    const showSenderInfo = !isOwn && isFirstInGroup &&
      (conversation.type === 'group' || conversation.type === 'custom');

    if (message.isSystemMessage) {
      return (
        <React.Fragment key={message._id}>
          {showDate && (
            <div className="flex items-center gap-3 my-4">
              <Separator className="flex-1" />
              <span className="text-xs text-muted-foreground font-medium px-2">{msgDate}</span>
              <Separator className="flex-1" />
            </div>
          )}
          <div className="flex justify-center my-2">
            <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
              {message.content}
            </span>
          </div>
        </React.Fragment>
      );
    }

    const sender = message.sender || { name: 'Deleted', lastName: 'User', role: 'student' };
    const role = roleConfig[sender.role];

    return (
      <React.Fragment key={message._id}>
        {showDate && (
          <div className="flex items-center gap-3 my-4">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground font-medium px-2">{msgDate}</span>
            <Separator className="flex-1" />
          </div>
        )}

        <div className={cn('flex items-end gap-2', isOwn ? 'flex-row-reverse' : 'flex-row', isLastInGroup ? 'mb-3' : 'mb-0.5')}>
          {/* Avatar */}
          <div className="w-8 flex-shrink-0">
            {!isOwn && isLastInGroup && (
              <Avatar className="w-8 h-8">
                {(sender.profilePicture?.url || sender.profilePicture?.data) && (
                  <AvatarImage
                    src={sender.profilePicture.url || `data:${sender.profilePicture.contentType};base64,${sender.profilePicture.data}`}
                    alt={sender.name}
                  />
                )}
                <AvatarFallback className="text-[11px] bg-primary/10 text-primary font-semibold">
                  {getInitials(`${sender.name} ${sender.lastName}`)}
                </AvatarFallback>
              </Avatar>
            )}
          </div>

          {/* Bubble */}
          <div className={cn('flex flex-col max-w-[65%]', isOwn ? 'items-end' : 'items-start')}>
            {message.isPinned && (
              <div className="flex items-center gap-1 mb-0.5 px-1">
                <Pin className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">Pinned</span>
              </div>
            )}
            {showSenderInfo && (
              <div className="flex items-center gap-1.5 mb-1 px-1">
                <span className="text-xs font-semibold text-foreground">
                  {sender.name} {sender.lastName}
                </span>
                {role && (
                  <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full border', role.className)}>
                    {role.label}
                  </span>
                )}
              </div>
            )}

            <div className={cn(
              'group relative px-3.5 py-2.5',
              isOwn
                ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
                : 'bg-background border border-border shadow-sm text-foreground rounded-2xl rounded-bl-sm'
            )}>
              {editingMessageId === message._id ? (
                <div className="min-w-[200px]">
                  <textarea
                    className="w-full text-sm bg-transparent border border-border rounded p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                    value={editContent.replace(/<[^>]*>/g, '')}
                    onChange={(e) => setEditContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(message._id); }
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    rows={2}
                    autoFocus
                  />
                  <div className="flex items-center gap-1 mt-1">
                    <button onClick={() => handleSaveEdit(message._id)} className="text-xs text-primary hover:underline flex items-center gap-0.5">
                      <Check className="w-3 h-3" /> Save
                    </button>
                    <button onClick={handleCancelEdit} className="text-xs text-muted-foreground hover:underline flex items-center gap-0.5">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {message.content && (
                    <div
                      className="text-sm leading-relaxed break-words message-html-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(
                        isSearching && searchQuery ? highlightSearchTerm(message.content, searchQuery) : message.content
                      ) }}
                    />
                  )}
                </>
              )}

              {message.attachments?.length > 0 && (
                <div>{message.attachments.map((a, i) => renderAttachment(a, i))}</div>
              )}

              {/* Reactions display */}
              {message.reactions?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {message.reactions.map((reaction, i) => {
                    const hasReacted = reaction.users?.some(u => (u._id || u) === currentUser.id);
                    const userNames = reaction.users
                      ?.map(u => u.name ? `${u.name} ${u.lastName || ''}`.trim() : null)
                      .filter(Boolean)
                      .join(', ');
                    return (
                      <button
                        key={i}
                        onClick={() => onReactToMessage(message._id, reaction.emoji)}
                        title={userNames || undefined}
                        className={cn(
                          'inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full border transition-colors',
                          hasReacted
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : isOwn
                            ? 'bg-white/10 border-white/20 text-primary-foreground/80 hover:bg-white/20'
                            : 'bg-muted border-border hover:bg-muted/80'
                        )}
                      >
                        <span>{reaction.emoji}</span>
                        <span className="text-[10px] font-medium">{reaction.users?.length}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Time + Edited + Actions */}
              <div className="flex items-center justify-between gap-3 mt-1">
                <div className="flex items-center gap-1.5">
                  <span className={cn('text-[10px]', isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground')}>
                    {new Date(message.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {message.editHistory?.length > 0 && (
                    <span className={cn('text-[10px] italic', isOwn ? 'text-primary-foreground/50' : 'text-muted-foreground/70')}>
                      (edited)
                    </span>
                  )}
                </div>

                {(canDeleteMessage(message) || message.sender?._id === currentUser.id) && !message.isSystemMessage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className={cn(
                        'opacity-0 group-hover:opacity-100 p-0.5 rounded transition-opacity',
                        isOwn ? 'text-primary-foreground/60 hover:text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      )}>
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isOwn ? 'end' : 'start'} side="top">
                      {!message.isSystemMessage && (
                        <div className="px-2 py-1.5">
                          <div className="flex gap-0.5">
                            {REACTION_EMOJIS.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => onReactToMessage(message._id, emoji)}
                                className="hover:bg-muted rounded p-1 text-sm transition-colors"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {message.sender?._id === currentUser.id && !message.isSystemMessage && (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => handleStartEdit(message)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit message
                        </DropdownMenuItem>
                      )}
                      {canPinMessage() && !message.isSystemMessage && (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => onPinMessage(message._id)}
                        >
                          <Pin className="w-3.5 h-3.5" />
                          {message.isPinned ? 'Unpin message' : 'Pin message'}
                        </DropdownMenuItem>
                      )}
                      {canDeleteMessage(message) && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive focus:bg-destructive/10 gap-2"
                          onClick={() => handleDeleteMessage(message._id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete message
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  });

  return (
    <TooltipProvider>
      <div
        key={`${conversation?.id}-${forceRender}`}
        className={cn('flex-1 flex flex-col bg-background', showChat ? 'flex' : 'hidden md:flex')}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBackToUsers}
              className="md:hidden h-8 w-8"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>

            {conversation.type === 'group' ? (
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Hash className="w-5 h-5 text-primary" />
              </div>
            ) : conversation.type === 'custom' ? (
              <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-500" />
              </div>
            ) : (
              <Avatar className="w-10 h-10">
                {(conversation.profilePicture?.url || conversation.profilePicture?.data) && (
                  <AvatarImage
                    src={conversation.profilePicture.url || `data:${conversation.profilePicture.contentType};base64,${conversation.profilePicture.data}`}
                    alt={conversation.name}
                  />
                )}
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                  {getInitials(conversation.name)}
                </AvatarFallback>
              </Avatar>
            )}

            <div>
              <h3 className="font-semibold text-sm text-foreground leading-tight">{conversation.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {conversation.type === 'group'
                  ? 'Group Chat'
                  : conversation.type === 'custom'
                  ? `${conversation.participants?.length || 0} participants`
                  : 'Direct Message'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowSearch(!showSearch);
                    if (showSearch) { setSearchQuery(''); onClearSearch?.(); }
                  }}
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                >
                  <SearchIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Search messages</TooltipContent>
            </Tooltip>

          {conversation.type === 'custom' && onManageMembers && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onManageMembers(conversation)}
                  key={`members-btn-${conversation.id}-${currentPermissions.canManage}-${forceRender}`}
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                >
                  {currentPermissions.canManage ? <Settings className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{currentPermissions.canManage ? 'Manage members' : 'View members'}</TooltipContent>
            </Tooltip>
          )}
          </div>
        </div>

        {/* ── Search bar ── */}
        {showSearch && (
          <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
            <SearchIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                onSearch?.(e.target.value);
              }}
              placeholder="Search in conversation..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
            {searchQuery && (
              <span className="text-xs text-muted-foreground">
                {searchResults?.length || 0} results
              </span>
            )}
            <button
              onClick={() => { setShowSearch(false); setSearchQuery(''); onClearSearch?.(); }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ── Pinned messages (always visible) ── */}
        {messages.some(m => m.isPinned) && (
          <div className="px-4 py-2 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Pin className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Pinned Messages</span>
              <span className="text-[10px] text-amber-500">({messages.filter(m => m.isPinned).length})</span>
            </div>
            <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
              {messages.filter(m => m.isPinned).map(msg => (
                <div key={msg._id} className="text-xs p-2 bg-background rounded border border-border">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="font-semibold">{msg.sender?.name} {msg.sender?.lastName}</span>
                    <span className="text-muted-foreground text-[10px]">
                      {new Date(msg.pinnedAt || msg.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div
                    className="text-muted-foreground line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(msg.content) }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Messages ── */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 bg-muted/20">
          <div className="px-4 py-4">
            {hasMore && (
              <div className="flex justify-center mb-4">
                <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loading} className="rounded-full text-xs h-7">
                  {loading ? 'Loading…' : 'Load earlier messages'}
                </Button>
              </div>
            )}

            {isSearching && searchResults?.length > 0 && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Search results ({searchResults.length})
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {searchResults.map(result => (
                    <div key={result._id} className="text-xs p-2 bg-background rounded border border-border">
                      <div className="flex items-center gap-1 mb-1">
                        <span className="font-semibold">{result.sender?.name} {result.sender?.lastName}</span>
                        <span className="text-muted-foreground">
                          {new Date(result.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div
                        className="text-muted-foreground line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(highlightSearchTerm(result.content, searchQuery)) }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <MessageCircle className="w-7 h-7 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">No messages yet</p>
                <p className="text-xs text-muted-foreground mt-1">Be the first to say hello to {conversation.name}!</p>
              </div>
            ) : (
              renderedMessages
            )}

          </div>
        </ScrollArea>

        {/* ── Scroll to bottom ── */}
        {showScrollBottom && (
          <div className="relative">
            <button
              onClick={scrollToBottom}
              className="absolute right-4 -top-12 z-10 w-8 h-8 bg-background border border-border shadow-md rounded-full flex items-center justify-center hover:bg-muted transition-colors"
            >
              <ArrowDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        )}

        {/* ── Typing indicator ── */}
        {typingUsers && Object.keys(typingUsers).length > 0 && (
          <div className="px-4 py-1 text-xs text-muted-foreground italic">
            {(() => {
              const names = Object.values(typingUsers);
              if (names.length === 1) return `${names[0]} is typing...`;
              if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
              return `${names[0]} and ${names.length - 1} others are typing...`;
            })()}
          </div>
        )}

        {/* ── Input ── */}
        <div className="px-4 py-3 border-t border-border bg-background">
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2 p-2.5 bg-muted rounded-lg">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center gap-1.5 bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs">
                  <Paperclip className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  <span className="max-w-[100px] truncate text-foreground">{file.name}</span>
                  <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                  <button
                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                    className="text-muted-foreground hover:text-destructive transition-colors ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-9 w-9 text-muted-foreground hover:text-foreground flex-shrink-0 mb-0.5"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Attach file</TooltipContent>
            </Tooltip>

            <div className="flex-1 chat-quill-wrapper" onKeyDown={handleQuillKeyDown}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={messageInput}
                onChange={(val) => { setMessageInput(val); if (onTyping) onTyping(); }}
                modules={chatQuillModules}
                formats={chatQuillFormats}
                placeholder={`Message ${conversation.name}…`}
              />
            </div>

            <Button
              type="button"
              size="icon"
              onClick={handleSendMessage}
              disabled={isQuillEmpty(messageInput) && attachments.length === 0}
              className="h-9 w-9 rounded-xl flex-shrink-0 mb-0.5"
            >
              <Send className="w-4 h-4" />
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ChatArea;
